// CourseList.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, deleteDoc, addDoc, updateDoc, query, where } from 'firebase/firestore';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { 
  Fab, 
  IconButton, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Button, 
  TextField, 
  Typography, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Alert, 
  Snackbar,
  Box,
  Paper,
  CircularProgress
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from "@mui/icons-material/Add";
import ListIcon from '@mui/icons-material/List';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {db} from '../Configuration';
import { useFirebaseData } from '../contexts/FirebaseDataContext';
import OutlinedInput from '@mui/material/OutlinedInput';
import { v4 as uuidv4 } from 'uuid';
import { buildInstructorByIdMap, getDisplayInstructorName } from '../utils/instructorDisplay';
import { isUrduText, getUrduSxProps } from '../utils/urduFont';

const NEW_TEACHER_OPTION = '__new_teacher__';

const CourseList = () => {
  const {
    campuses: contextCampuses,
    instructors: contextInstructors,
    initialLoadComplete,
    refreshCollection,
  } = useFirebaseData();
  const [courses, setCourses] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openTopicsDialog, setOpenTopicsDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [topics, setTopics] = useState([]);
  const [newTopic, setNewTopic] = useState('');
  const [courseName, setCourseName] = useState('');
  const [courseInstructor, setCourseInstructor] = useState('');
  const [selectedInstructorId, setSelectedInstructorId] = useState('');
  const [selectedCampuses, setSelectedCampuses] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState('');
  const [totalClasses, setTotalClasses] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const instructorById = useMemo(
    () => buildInstructorByIdMap(contextInstructors),
    [contextInstructors]
  );

  const fetchCourses = useCallback(async () => {
    try {
      const courseCollection = collection(db, 'Courses');
      const courseSnapshot = await getDocs(courseCollection);
      let courseList = courseSnapshot.docs.map((doc) => ({
        id: doc.data().id || doc.id, // Use data field id for display, fallback to document id
        firestoreId: doc.id, // Store the actual Firestore document ID
        ...doc.data(),
      }));
      
      // Filter courses by selected campus
      if (selectedCampus) {
        courseList = courseList.filter(course => 
          course.campusCodes && course.campusCodes.includes(selectedCampus)
        );
      } else {
        courseList = [];
      }
      
      setCourses(courseList);
      
      if (courseList.length === 0 && selectedCampus) {
        showSnackbar('No courses found for the selected campus. Add your first course!', 'info');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      showSnackbar('Error loading courses. Please refresh the page.', 'error');
    }
  }, [selectedCampus]);

  const fetchCampuses = useCallback(async () => {
    try {
        const campusSnapshot = await getDocs(collection(db, 'Campus'));
        
        let campusList = campusSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));

        // Get allowed campuses from localStorage and parse it as an array
        let allowedCampus = JSON.parse(localStorage.getItem('campusCode')) || [];

        // If allowedCampus is not empty, filter the campus list
        if (allowedCampus.length > 0) {
            campusList = campusList.filter(campus => allowedCampus.includes(campus.campusCode));
        }

        setCampuses(campusList);
        
        if (campusList.length === 0) {
          showSnackbar('No campuses available. Please add campuses first.', 'warning');
        }
    } catch (error) {
        console.error('Error fetching campuses:', error);
        showSnackbar('Error loading campuses. Please refresh the page.', 'error');
    }
}, []);

  const getNextId = async () => {
    // Fetch all courses and find the highest `id`
    const courseCollection = collection(db, 'Courses');
    const courseSnapshot = await getDocs(courseCollection);
    const courseList = courseSnapshot.docs.map((doc) => doc.data());
    const maxId = courseList.reduce((max, course) => Math.max(max, parseInt(course.id, 10)), 0);
    return (maxId + 1).toString(); // Increment by 1 and return as string
  };

  const validateForm = () => {
    if (!courseName.trim()) {
      showSnackbar('Course name is required', 'error');
      return false;
    }
    if (!selectedInstructorId) {
      showSnackbar('Select a teacher or choose "Type new name"', 'error');
      return false;
    }
    if (selectedInstructorId === NEW_TEACHER_OPTION && !courseInstructor.trim()) {
      showSnackbar('Enter the new teacher name', 'error');
      return false;
    }
    if (selectedCampuses.length === 0) {
      showSnackbar('Please select at least one campus', 'error');
      return false;
    }
    return true;
  };

  /** Edit subject (course): only teacher may change; name / total classes / campuses stay fixed. */
  const validateEditCourseTeacherOnly = () => {
    if (!selectedInstructorId) {
      showSnackbar('Select a teacher or choose "Type new name"', 'error');
      return false;
    }
    if (selectedInstructorId === NEW_TEACHER_OPTION && !courseInstructor.trim()) {
      showSnackbar('Enter the new teacher name', 'error');
      return false;
    }
    return true;
  };

  const formatCampusCodesForDisplay = (codes) => {
    if (!codes || !Array.isArray(codes) || codes.length === 0) return '—';
    return codes
      .map((code) => {
        const c = campuses.find((x) => x.campusCode === code);
        return c ? `${c.campusName} (${code})` : code;
      })
      .join(', ');
  };

  const resolveInstructorForSave = async (nameFallback = '') => {
    if (selectedInstructorId === NEW_TEACHER_OPTION) {
      const name = courseInstructor.trim();
      const existing = contextInstructors.find(
        (i) => (i.name || '').trim().toLowerCase() === name.toLowerCase()
      );
      if (existing) {
        return { instructorId: existing.id, instructorName: (existing.name || '').trim() };
      }
      const ref = await addDoc(collection(db, 'Instructors'), {
        name,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      await refreshCollection('instructors');
      return { instructorId: ref.id, instructorName: name };
    }
    const linked = contextInstructors.find((i) => i.id === selectedInstructorId);
    return {
      instructorId: selectedInstructorId,
      instructorName: (linked?.name || courseInstructor || nameFallback || '').trim(),
    };
  };

  const handleAddCourse = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const nextId = await getNextId();
      const { instructorId, instructorName } = await resolveInstructorForSave();
      const courseData = {
        id: nextId,
        name: courseName,
        instructor: instructorName,
        totalClasses: parseInt(totalClasses),
        campusCodes: selectedCampuses,
        instructorId,
      };

      await addDoc(collection(db, 'Courses'), courseData);
      showSnackbar(`Course "${courseName}" added successfully`, 'success');
      setOpenAddDialog(false);
      fetchCourses();
    } catch (error) {
      console.error('Error adding course:', error);
      showSnackbar('Error adding course. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCourse = async () => {
    if (!validateEditCourseTeacherOnly()) return;

    setSubmitting(true);
    try {
      // Use the Firestore document ID, not the data field id
      const courseRef = doc(db, 'Courses', selectedCourse.firestoreId || selectedCourse.id);
      const { instructorId, instructorName } = await resolveInstructorForSave(
        selectedCourse?.instructor || ''
      );
      const courseData = {
        instructor: instructorName,
        instructorId,
      };

      await updateDoc(courseRef, courseData);
      showSnackbar(`Teacher updated for "${selectedCourse?.name || courseName}"`, 'success');
      setOpenEditDialog(false);
      fetchCourses();
    } catch (error) {
      console.error('Error updating course:', error);
      showSnackbar('Error updating course. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if course needs predefined topics
  const needsPredefinedTopics = (courseName) => {
    if (!courseName) return false;
    const normalizedName = courseName.trim().toLowerCase();
    return normalizedName === 'special lectures' || 
           (normalizedName.includes('fikr') && normalizedName.includes('islami'));
  };

  // Fetch topics for a course from Topics collection
  const fetchTopicsForCourse = async (courseName) => {
    try {
      const topicsRef = collection(db, 'Topics');
      const q = query(topicsRef, where('courseName', '==', courseName));
      const querySnapshot = await getDocs(q);
      const topicsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        topicId: doc.data().topicId || doc.id,
        name: doc.data().name
      }));
      return topicsList;
    } catch (error) {
      console.error('Error fetching topics:', error);
      return [];
    }
  };

  // Handle opening topics dialog
  const handleOpenTopicsDialog = async (course) => {
    setSelectedCourse(course);
    const fetchedTopics = await fetchTopicsForCourse(course.name);
    setTopics(fetchedTopics);
    setNewTopic('');
    setOpenTopicsDialog(true);
  };

  // Handle adding a new topic
  const handleAddTopic = async () => {
    if (!newTopic.trim() || !selectedCourse) return;
    
    // Check if topic already exists
    const topicExists = topics.some(t => t.name.toLowerCase() === newTopic.trim().toLowerCase());
    if (topicExists) {
      showSnackbar('Topic already exists', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const topicId = uuidv4();
      const topicData = {
        topicId: topicId,
        name: newTopic.trim(),
        courseName: selectedCourse.name,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'Topics'), topicData);
      // Refresh topics list
      const fetchedTopics = await fetchTopicsForCourse(selectedCourse.name);
      setTopics(fetchedTopics);
      setNewTopic('');
      showSnackbar(`Topic "${newTopic.trim()}" added successfully`, 'success');
    } catch (error) {
      console.error('Error adding topic:', error);
      showSnackbar('Error adding topic. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle deleting a topic
  const handleDeleteTopic = async (topicDocId) => {
    const topic = topics.find(t => t.id === topicDocId);
    if (!topic) return;
    
    if (window.confirm(`Are you sure you want to delete topic "${topic.name}"?`)) {
      try {
        await deleteDoc(doc(db, 'Topics', topicDocId));
        // Refresh topics list
        const fetchedTopics = await fetchTopicsForCourse(selectedCourse.name);
        setTopics(fetchedTopics);
        showSnackbar(`Topic "${topic.name}" deleted successfully`, 'success');
      } catch (error) {
        console.error('Error deleting topic:', error);
        showSnackbar('Error deleting topic. Please try again.', 'error');
      }
    }
  };

  const handleDeleteCourse = async (id) => {
    const course = courses.find(c => c.id === id);
    if (window.confirm(`Are you sure you want to delete course "${course?.name}"? This action cannot be undone.`)) {
      try {
        // Use the Firestore document ID for deletion
        const documentId = course?.firestoreId || id;
        await deleteDoc(doc(db, 'Courses', documentId));
        showSnackbar(`Course "${course?.name}" deleted successfully`, 'success');
        fetchCourses();
      } catch (error) {
        console.error('Error deleting course:', error);
        showSnackbar('Error deleting course. Please try again.', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Auto-select campus if only one is available
  useEffect(() => {
    if (initialLoadComplete && contextCampuses.length === 1 && !selectedCampus) {
      setSelectedCampus(contextCampuses[0].campusCode);
    }
  }, [initialLoadComplete, contextCampuses, selectedCampus]);

  const handleCampusChange = (event) => {
    setSelectedCampus(event.target.value);
  };

  useEffect(() => {
    fetchCampuses();
  }, [fetchCampuses]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (openAddDialog) {
      setCourseName('');
      setCourseInstructor('');
      setSelectedInstructorId('');
      setTotalClasses('');
      setSelectedCampuses([]);
    }
  }, [openAddDialog, openEditDialog]);

  const columns = [
    { field: 'name', headerName: 'Course Name', width: 200 },
    {
      field: 'instructor',
      headerName: 'Instructor',
      width: 200,
      valueGetter: (value, row) =>
        getDisplayInstructorName(row, instructorById) || row.instructor || '',
    },
    { field: 'totalClasses', headerName: 'Total Classes', width: 150 },
    { 
      field: 'campusCodes', 
      headerName: 'Campus Codes', 
      width: 250, 
      renderCell: (params) => (
        <div>
          {Array.isArray(params.value) ? params.value.join(", ") : params.value}
        </div>
      ) 
    }, // Show multiple campuses
    {
      disableExport:true,
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (params) => (
        <>
          {needsPredefinedTopics(params.row.name) && (
            <IconButton
              color="info"
              onClick={() => handleOpenTopicsDialog(params.row)}
              aria-label="manage topics"
              title="Manage Topics"
              sx={{
                borderRadius: 2,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ListIcon />
            </IconButton>
          )}
          <IconButton
            color="primary"
            onClick={() => {
              setSelectedCourse(params.row);
              setCourseName(params.row.name);
              if (params.row.instructorId) {
                setSelectedInstructorId(params.row.instructorId);
                setCourseInstructor(params.row.instructor || '');
              } else {
                setSelectedInstructorId(NEW_TEACHER_OPTION);
                setCourseInstructor(params.row.instructor || '');
              }
              setTotalClasses(params.row.totalClasses);
              setSelectedCampuses(params.row.campusCodes || []);
              setOpenEditDialog(true);
            }}
            aria-label="edit"
            sx={{
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => handleDeleteCourse(params.row.id)}
            aria-label="delete"
            sx={{
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#2E7D32',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📚 Subjects Configuration
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
          Manage course subjects, assign instructors, and configure campus assignments
        </Typography>
      </Box>

      {/* Campus Selection */}
      <Paper elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
          <InputLabel sx={{ color: '#4CAF50' }}>Select Campus</InputLabel>
          <Select
            value={selectedCampus}
            onChange={handleCampusChange}
            input={<OutlinedInput label="Select Campus" />}
            disabled={contextCampuses.length === 1}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#4CAF50',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2E7D32',
                },
              },
            }}
          >
            {contextCampuses.map((campus) => (
              <MenuItem key={campus.id} value={campus.campusCode}>
                {campus.campusName} ({campus.campusCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {!selectedCampus && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" color="text.secondary">
            Please select a campus to view courses
          </Typography>
        </Paper>
      )}

      {/* Courses Data Grid Card */}
      {selectedCampus && (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(76, 175, 80, 0.1)'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#2E7D32',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📊 Course Records
        </Typography>
        
        <DataGrid
          rows={courses}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          sx={{
            border: 'none',
            height: 600,
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #4CAF50',
            },
          }}
        />
      </Paper>
      )}

      {/* Floating Action Button */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 20, 
        right: 20, 
        zIndex: 1000
      }}>
        <Fab 
          color="primary" 
          aria-label="add" 
          onClick={() => setOpenAddDialog(true)}
          sx={{
            backgroundColor: '#4CAF50',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#2E7D32',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* Add Course Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add New Course</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Course Name"
            type="text"
            fullWidth
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 1 }}>
            <InputLabel id="add-instructor-select-label">Teacher</InputLabel>
            <Select
              labelId="add-instructor-select-label"
              label="Teacher"
              value={selectedInstructorId}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedInstructorId(v);
                if (v && v !== NEW_TEACHER_OPTION) {
                  const t = contextInstructors.find((i) => i.id === v);
                  if (t?.name) setCourseInstructor(t.name);
                }
              }}
              input={<OutlinedInput label="Teacher" />}
            >
              <MenuItem value="">
                <em>Select teacher…</em>
              </MenuItem>
              <MenuItem value={NEW_TEACHER_OPTION}>Type new name (adds to Teachers list)</MenuItem>
              {contextInstructors.map((inst) => (
                <MenuItem key={inst.id} value={inst.id}>
                  {inst.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedInstructorId === NEW_TEACHER_OPTION && (
            <TextField
              margin="dense"
              label="New teacher name"
              type="text"
              fullWidth
              value={courseInstructor}
              onChange={(e) => setCourseInstructor(e.target.value)}
              placeholder="Full name"
              helperText="Saved to the Teachers list when you add this course."
            />
          )}
          <TextField
            margin="dense"
            label="Total Classes"
            type="number"
            fullWidth
            value={totalClasses}
            onChange={(e) => setTotalClasses(e.target.value)}
          />
          <FormControl fullWidth style={{ marginTop: 10 }}>
            <InputLabel>Select Campuses</InputLabel>
            <Select multiple value={selectedCampuses} onChange={(e) => setSelectedCampuses(e.target.value)} renderValue={(selected) => selected.join(", ")}>
              {campuses.map((campus) => (
                <MenuItem key={campus.id} value={campus.campusCode}>
                  {campus.campusName} ({campus.campusCode})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)} color="primary" disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddCourse} 
            color="primary" 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit Course</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 0.5 }}>
            Only the teacher can be changed. Course name, total classes, and campuses cannot be edited here.
          </Typography>
          <TextField
            margin="dense"
            label="Course Name"
            type="text"
            fullWidth
            value={courseName}
            InputProps={{ readOnly: true }}
            sx={{
              mt: 0.5,
              '& .MuiInputBase-input': {
                fontFamily: isUrduText(courseName)
                  ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                  : 'inherit',
              },
            }}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 1 }}>
            <InputLabel id="edit-instructor-select-label">Teacher</InputLabel>
            <Select
              autoFocus
              labelId="edit-instructor-select-label"
              label="Teacher"
              value={selectedInstructorId}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedInstructorId(v);
                if (v && v !== NEW_TEACHER_OPTION) {
                  const t = contextInstructors.find((i) => i.id === v);
                  if (t?.name) setCourseInstructor(t.name);
                }
              }}
              input={<OutlinedInput label="Teacher" />}
            >
              <MenuItem value="">
                <em>Select teacher…</em>
              </MenuItem>
              <MenuItem value={NEW_TEACHER_OPTION}>Type new name (adds to Teachers list)</MenuItem>
              {contextInstructors.map((inst) => (
                <MenuItem key={inst.id} value={inst.id}>
                  {inst.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedInstructorId === NEW_TEACHER_OPTION && (
            <TextField
              margin="dense"
              label="New teacher name"
              type="text"
              fullWidth
              value={courseInstructor}
              onChange={(e) => setCourseInstructor(e.target.value)}
              placeholder="Full name"
              helperText="Saved to the Teachers list when you save this course."
            />
          )}
          <TextField
            margin="dense"
            label="Total Classes"
            type="number"
            fullWidth
            value={totalClasses}
            InputProps={{ readOnly: true }}
          />
          <TextField
            margin="dense"
            label="Campuses"
            fullWidth
            value={formatCampusCodesForDisplay(selectedCampuses)}
            InputProps={{ readOnly: true }}
            multiline
            maxRows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} color="primary" disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditCourse} 
            color="primary" 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Topics Dialog */}
      <Dialog 
        open={openTopicsDialog} 
        onClose={() => setOpenTopicsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Topics - {selectedCourse?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add, edit, or remove predefined topics for this course. These topics will appear in the attendance form.
            </Typography>
            
            {/* Add New Topic */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <TextField
                fullWidth
                label="New Topic"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddTopic();
                  }
                }}
                placeholder="Enter topic name"
              />
              <Button
                variant="contained"
                onClick={handleAddTopic}
                disabled={!newTopic.trim() || submitting || topics.some(t => t.name.toLowerCase() === newTopic.trim().toLowerCase())}
                sx={{
                  backgroundColor: '#4CAF50',
                  '&:hover': {
                    backgroundColor: '#2E7D32',
                  },
                }}
              >
                Add
              </Button>
            </Box>

            {/* Topics List */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Topics ({topics.length})
            </Typography>
            {topics.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2" color="text.secondary">
                  No topics added yet. Add your first topic above.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {topics.map((topic) => (
                  <Paper
                    key={topic.id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#f8f9fa',
                      '&:hover': {
                        backgroundColor: '#e9ecef',
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        className={isUrduText(topic.name || '') ? 'urdu' : ''}
                        sx={{
                          ...getUrduSxProps(topic.name || ''),
                          direction: 'ltr',
                          textAlign: 'left'
                        }}
                      >
                        {topic.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {topic.topicId}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteTopic(topic.id)}
                      aria-label="delete topic"
                      disabled={submitting}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenTopicsDialog(false)} 
            color="primary"
            disabled={submitting}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseList;
